import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import newNotificationSound from "../../assets/images/new_notification.wav";

const STORAGE_KEY = "audioUnlocked";
const REMINDER_DELAY_MS = 8000;   // wait this long before nudging the user
const TOAST_ID = "audio-unlock";

/**
 * Silently unlocks browser audio on the user's first interaction — no popup.
 * If the user hasn't interacted after ~8s AND audio is still blocked, a small
 * toast reminder appears (dismissible) instead of a full-screen modal.
 */
function AudioUnlocker({ onUnlock }) {
    const unlockedRef = useRef(false);
    const reminderTimerRef = useRef(null);

    useEffect(() => {
        // Already unlocked this session — nothing to do
        if (sessionStorage.getItem(STORAGE_KEY) === "1") {
            unlockedRef.current = true;
            onUnlock(true);
            return;
        }

        let cancelled = false;

        const markUnlocked = () => {
            if (cancelled || unlockedRef.current) return;
            unlockedRef.current = true;
            sessionStorage.setItem(STORAGE_KEY, "1");
            onUnlock(true);
            clearTimeout(reminderTimerRef.current);
            toast.dismiss(TOAST_ID);
            removeListeners();
        };

        const attemptSilentUnlock = () => {
            const a = new Audio(newNotificationSound);
            a.volume = 0;
            a.play()
                .then(markUnlocked)
                .catch(() => { /* still blocked — wait for user interaction */ });
        };

        // 1. Try autoplay once — works if the tab was already interacted with
        attemptSilentUnlock();

        // 2. Fallback — unlock on the user's first pointer/key/touch anywhere
        const handleFirstInteraction = () => attemptSilentUnlock();
        const opts = { once: false, capture: true, passive: true };
        window.addEventListener("pointerdown", handleFirstInteraction, opts);
        window.addEventListener("keydown",     handleFirstInteraction, opts);
        window.addEventListener("touchstart",  handleFirstInteraction, opts);

        function removeListeners() {
            window.removeEventListener("pointerdown", handleFirstInteraction, true);
            window.removeEventListener("keydown",     handleFirstInteraction, true);
            window.removeEventListener("touchstart",  handleFirstInteraction, true);
        }

        // 3. If still blocked after a delay, nudge with a small toast
        reminderTimerRef.current = setTimeout(() => {
            if (cancelled || unlockedRef.current) return;
            toast(
                (t) => (
                    <span
                        role="button"
                        onClick={() => {
                            attemptSilentUnlock();
                            // Fallback: even a click on this card itself is a user gesture
                            setTimeout(() => {
                                if (!unlockedRef.current) attemptSilentUnlock();
                            }, 60);
                            toast.dismiss(t.id);
                        }}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#0f172a",
                            cursor: "pointer",
                            userSelect: "none",
                        }}
                    >
                        <span>🔔 Tap anywhere to enable notification sound</span>
                    </span>
                ),
                {
                    id: TOAST_ID,
                    duration: Infinity,          // stays until user acts or dismisses
                    position: "bottom-right",
                    style: {
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 6px 20px rgba(15,23,42,0.10)",
                        padding: "10px 12px",
                        cursor: "pointer",
                    },
                }
            );
        }, REMINDER_DELAY_MS);

        return () => {
            cancelled = true;
            clearTimeout(reminderTimerRef.current);
            toast.dismiss(TOAST_ID);
            removeListeners();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}

export default AudioUnlocker;
